import { State, StateView } from 'components';
import { useAcceptLegalTexts } from 'hooks/legal/useAcceptLegalTexts';
import { useGetLegalTextsByIdentifier } from 'hooks/legal/useGetCurrentLegalTextStatus';
import { FC, useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { hasValue, isEmptyOrNull } from 'utils';

import './LegalConfirmation.scss';

export const LegalConfirmation: FC = () => {
  const [errors, setErrors] = useState<string[]>();

  const [searchParams] = useSearchParams();

  const legalTextIdentifiers = useMemo(
    () =>
      searchParams
        .getAll('legalTextIdentifiers')
        .flatMap((s) => s.split(',').filter((i) => !isEmptyOrNull(i))),
    [searchParams]
  );

  const mailToken = useMemo(
    () => searchParams.get('token') ?? undefined,
    [searchParams]
  );
  const personId = useMemo(
    () => searchParams.get('person') ?? undefined,
    [searchParams]
  );

  const {
    data: legalTextStatus,
    isFetching: isLegalTextPending,
    isError
  } = useGetLegalTextsByIdentifier(legalTextIdentifiers);

  const {
    mutate: accept,
    isPending: isAcceptLoading,
    isSuccess
  } = useAcceptLegalTexts();

  const legalUrls = useMemo(() => {
    const urls: { urlText: string; name: string; dateActive?: number }[] = [];

    if (!hasValue(legalTextStatus)) return urls;

    for (const { urlText, name, dateActive } of legalTextStatus) {
      if (!isEmptyOrNull(urlText) && !isEmptyOrNull(name)) {
        urls.push({ urlText, name, dateActive });
      }
    }

    return urls;
  }, [legalTextStatus]);

  const handleAccept = useCallback(() => {
    if (isEmptyOrNull(mailToken) || isEmptyOrNull(personId)) return;

    const legalTextIdentifiersToAccept: string[] = [];

    if (!hasValue(legalTextStatus)) return;

    for (const { legalTextIdentifier } of legalTextStatus) {
      if (!isEmptyOrNull(legalTextIdentifier)) {
        legalTextIdentifiersToAccept.push(legalTextIdentifier);
      }
    }

    if (!hasValue(legalTextIdentifiersToAccept)) return;
    accept(
      {
        mailToken,
        clientLegalTextAcceptMailRequest: {
          personId,
          legalTextIdentifiersToAccept
        }
      },
      {
        onError: () => {
          setErrors([
            'Hoppla, etwas lief schief.\nBitte versuch es zu einem späteren Zeitpunkt erneut.'
          ]);
        }
      }
    );
  }, [accept, legalTextStatus, mailToken, personId]);

  const isLoading = useMemo(
    () => isLegalTextPending || isAcceptLoading,
    [isAcceptLoading, isLegalTextPending]
  );

  if (isLoading) {
    return (
      <div className="legal-confirmation">
        <StateView state={State.Loading} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="legal-confirmation">
        <StateView
          state={State.Error}
          messages={[
            'Hoppla, etwas lief schief.\nBitte versuch es zu einem späteren Zeitpunkt erneut.'
          ]}
        />
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="legal-confirmation">
        <StateView
          state={State.Success}
          messages={[
            'Die neuen Nutzungsbedingungen und Datenschutzregelungen wurden erfolgreich akzeptiert.'
          ]}
        />
      </div>
    );
  }

  if (!hasValue(legalUrls)) {
    return (
      <div className="legal-confirmation">
        <StateView
          state={State.Success}
          messages={[
            <div key="information" className="information">
              <h3 className="title">
                Die neuen Nutzungsbedingungen und Datenschutzregelungen wurden
                bereits erfolgreich akzeptiert.
              </h3>
              <span className="accept-conformation">
                Du brauchst nichts mehr zu unternehmen
              </span>
            </div>
          ]}
        />
      </div>
    );
  }

  return (
    <div className="legal-confirmation">
      {hasValue(errors) && (
        <div className="messages">
          {errors?.map((errorMessage, index) => (
            <div className="message" key={index}>
              {errorMessage} <br />
            </div>
          ))}
          <button className="clear-button" onClick={() => setErrors([])}>
            x
          </button>
        </div>
      )}
      <div className="information">
        <h3 className="title">
          Neue Nutzerbedingungen und Datenschutzregelungen treten bald in Kraft
        </h3>
        <p className="description">
          Im Rahmen der Übernahme der App durch die Smartes Land GmbH müssen
          alle Nutzer den neuen Nutzerbedingungen des StadtLand.Funks zustimmen
          und über die neuen Datenschutzregelungen in Kenntnis gesetzt werden.
          Diese findest du weiter unten.
        </p>
        <span className="date">
          Wichtiger Hinweis:
          <br />
          Wenn du diesen nicht bis spätestens 31.12.2025 zustimmst, wird dein
          Konto gelöscht!
        </span>
      </div>
      {legalUrls.map(({ urlText, name, dateActive }) => (
        <div key={name} className="option-row">
          <span>{'>'}</span>
          <div className="option-values">
            <a
              href={urlText}
              target="_blank"
              rel="noreferrer"
              className="option"
            >
              {name}
            </a>
            {!isEmptyOrNull(dateActive) && (
              <span className="option-date">
                {` Gültig ab ${new Date(dateActive).toLocaleDateString('de-DE')}`}{' '}
              </span>
            )}
          </div>
        </div>
      ))}

      <br />
      <button
        className="accept-button"
        onClick={handleAccept}
        disabled={
          !hasValue(legalUrls) ||
          isEmptyOrNull(mailToken) ||
          isEmptyOrNull(personId)
        }
      >
        Zustimmen
      </button>
    </div>
  );
};
